/**
 * Shift export/import helpers for the dashboard.
 *
 * Mirrors the column shape used by `apps/operator/src/lib/csvExport.ts` and
 * `apps/operator/src/lib/csvImport.ts` so a CSV exported from one app can be
 * re-imported in the other. The operator lib is bound to its own LocalShiftLog
 * type via the @ alias, so the dashboard ports the structurally-equivalent
 * logic here rather than importing across app boundaries.
 *
 * Keep these column lists in sync with the operator's implementations if the
 * operator's shape ever changes.
 */
import JSZip from "jszip";
import type { ShiftRow } from "./api";

// Light shape mirroring LocalShiftLog. We only read what's needed for export
// and we keep importer output in this same shape so the page can wrap it for
// the API upsert call.
export interface ShiftDataLike {
  shiftDetails?: {
    site?: string;
    date?: string;
    shiftType?: "day" | "night" | string;
    operatorName?: string;
    rigId?: string;
    rigType?: string;
    startTime?: string;
    endTime?: string;
    location?: string;
    workAreas?: string[];
    patternIds?: string[];
  };
  drillHoles?: Array<{
    id?: string;
    holeNumber?: string;
    location?: string;
    depth?: number;
    diameter?: number;
    angle?: number;
    drillingTimeSeconds?: number;
    status?: string;
    notes?: string;
    workArea?: string;
    patternId?: string;
  }>;
  consumables?: Array<{
    id?: string;
    type?: string;
    manufacturer?: string;
    name?: string;
    serialNumber?: string;
    bitDiameter?: number;
    faceDesign?: string;
    metresDrilled?: number;
    photo?: string;
    linkedHoleIds?: string[];
  }>;
  delays?: Array<{
    id?: string;
    delayType?: string;
    description?: string;
    timeSeconds?: number;
    notes?: string;
    startedAt?: string;
    lastCompletedHoleId?: string;
  }>;
  notes?: string;
  createdAt?: string;
}

const CSV_HEADERS = [
  "Shift ID", "Date", "Shift Type", "Operator Name", "Rig ID", "Rig Type",
  "Start Time", "End Time", "Location", "Hole Number", "Hole Location",
  "Hole Depth (m)", "Hole Diameter (mm)", "Hole Angle (°)",
  "Drilling Time (seconds)", "Drilling Time (formatted)", "Hole Status", "Hole Notes",
  "Consumable Type", "Consumable Manufacturer", "Consumable Name",
  "Consumable Serial Number", "Bit Diameter (mm)", "Face Design",
  "Metres Drilled", "Shift Notes", "Created At",
];

function formatDrillingTime(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function escapeCSV(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function rowToData(r: ShiftRow): ShiftDataLike {
  return (r.shift_data ?? {}) as ShiftDataLike;
}

/** Build a CSV string for one shift; used both for the global CSV export and the per-shift CSV inside the ZIP. */
function buildCSVRowsForShift(r: ShiftRow): string[][] {
  const d = rowToData(r);
  const sd = d.shiftDetails ?? {};
  const holes = d.drillHoles ?? [];
  const consumables = d.consumables ?? [];

  const baseRow = [
    r.id, sd.date ?? "", sd.shiftType ?? "", sd.operatorName ?? "",
    sd.rigId ?? "", sd.rigType ?? "",
    sd.startTime ?? "", sd.endTime ?? "", sd.location ?? "",
  ];

  const out: string[][] = [];
  if (holes.length > 0) {
    holes.forEach((hole) => {
      const drillingSeconds = hole.drillingTimeSeconds ?? 0;
      out.push([
        ...baseRow, hole.holeNumber ?? "", hole.location ?? "",
        String(hole.depth ?? ""), String(hole.diameter ?? ""), String(hole.angle ?? ""),
        String(drillingSeconds), formatDrillingTime(drillingSeconds),
        hole.status ?? "", hole.notes ?? "",
        "", "", "", "", "", "", "",
        d.notes ?? "", d.createdAt ?? r.created_at,
      ]);
    });
  }
  if (consumables.length > 0) {
    consumables.forEach((c) => {
      out.push([
        ...baseRow, "", "", "", "", "", "", "", "",
        c.type ?? "", c.manufacturer ?? "", c.name ?? "",
        c.serialNumber ?? "",
        c.bitDiameter ? String(c.bitDiameter) : "",
        c.faceDesign ?? "", String(c.metresDrilled ?? 0),
        d.notes ?? "", d.createdAt ?? r.created_at,
      ]);
    });
  }
  if (holes.length === 0 && consumables.length === 0) {
    out.push([
      ...baseRow, "", "", "", "", "", "", "", "",
      "", "", "", "", "", "", "",
      d.notes ?? "", d.createdAt ?? r.created_at,
    ]);
  }
  return out;
}

export function exportShiftRowsToCSV(rows: ShiftRow[]): string | null {
  if (rows.length === 0) return null;
  const dataRows = rows.flatMap(buildCSVRowsForShift);
  return [
    CSV_HEADERS.map(escapeCSV).join(","),
    ...dataRows.map((row) => row.map(escapeCSV).join(",")),
  ].join("\n");
}

export function exportShiftRowToCSV(row: ShiftRow): string {
  const dataRows = buildCSVRowsForShift(row);
  return [
    CSV_HEADERS.map(escapeCSV).join(","),
    ...dataRows.map((r) => r.map(escapeCSV).join(",")),
  ].join("\n");
}

export function downloadFile(content: BlobPart, filename: string, mime: string): void {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * ZIP layout matches the operator's `zipExport.ts`: photos folder for
 * drill-bit/hammer photos plus a single `shift_logs.csv`. We also drop a
 * lightweight `summary.txt` so an operator looking inside the zip can see
 * which shifts are inside without parsing CSV.
 */
export async function exportShiftRowsToZip(rows: ShiftRow[]): Promise<Blob | null> {
  if (rows.length === 0) return null;
  const zip = new JSZip();
  const photosFolder = zip.folder("photos");

  rows.forEach((r) => {
    const d = rowToData(r);
    const sd = d.shiftDetails ?? {};
    (d.consumables ?? []).forEach((c) => {
      if (c.photo && (c.type === "drill-bit" || c.type === "hammer")) {
        const prefix = c.type === "drill-bit" ? "drillbit" : "hammer";
        const filename = `${prefix}_${c.serialNumber || c.id || "unknown"}_${sd.date ?? "nodate"}.jpg`;
        const base64 = c.photo.replace(/^data:image\/\w+;base64,/, "");
        photosFolder?.file(filename, base64, { base64: true });
      }
    });
  });

  const csvContent = exportShiftRowsToCSV(rows);
  if (csvContent) zip.file("shift_logs.csv", csvContent);

  // Per-shift summary so users can audit the export contents quickly.
  const summary = rows.map((r) => {
    const d = rowToData(r);
    const sd = d.shiftDetails ?? {};
    const holes = (d.drillHoles ?? []).length;
    const metres = (d.drillHoles ?? []).reduce((s, h) => s + (h.depth ?? 0), 0);
    return `${r.id} | ${sd.date ?? "?"} | ${sd.operatorName ?? "?"} | ${sd.rigId ?? "?"} | ${holes} holes | ${metres.toFixed(1)}m | ${r.status}`;
  }).join("\n");
  zip.file("summary.txt", `Strataworks shift export — ${rows.length} shift(s)\n\n${summary}\n`);

  return await zip.generateAsync({ type: "blob" });
}

// ---------- Import ----------

/**
 * Best-effort: coerce common spreadsheet date formats to ISO `YYYY-MM-DD` so
 * the dashboard's parseISO/format pipeline doesn't trip on Aussie `DD/MM/YYYY`
 * exports. Returns the input untouched if it doesn't match anything we know
 * (and the row will still upload — just won't show a formatted date).
 */
function normalizeDate(value: string): string {
  const v = value.trim();
  if (!v) return v;
  // Already ISO (YYYY-MM-DD or full timestamp)
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v;
  // DD/MM/YYYY or D/M/YYYY  (common Excel/AU export)
  let m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  // DD-MM-YYYY
  m = v.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return v;
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      result.push(current); current = "";
    } else {
      current += char;
    }
  }
  result.push(current);
  return result;
}

export interface ImportedShift {
  id: string;
  shift_data: ShiftDataLike;
}

/**
 * Parse a CSV (matching the operator's column layout) into ShiftRow upsert
 * payloads. The dashboard then POSTs each one to /api/shifts via shifts.upsert.
 */
export function importShiftLogsFromCSV(csvText: string): { logs: ImportedShift[]; errors: string[] } {
  const lines = csvText.trim().split(/\r?\n/);
  if (lines.length < 2) return { logs: [], errors: ["CSV file is empty or has no data rows."] };

  const errors: string[] = [];
  const shiftMap = new Map<string, string[][]>();

  lines.slice(1).filter((l) => l.trim()).forEach((line, idx) => {
    const cols = parseCSVLine(line);
    const shiftId = cols[0]?.trim();
    if (!shiftId) {
      errors.push(`Row ${idx + 2}: Missing Shift ID — skipped.`);
      return;
    }
    if (!shiftMap.has(shiftId)) shiftMap.set(shiftId, []);
    shiftMap.get(shiftId)!.push(cols);
  });

  const logs: ImportedShift[] = [];
  const now = new Date().toISOString();

  shiftMap.forEach((rows, shiftIdFromCSV) => {
    const first = rows[0];
    // Backend requires UUIDs (idempotent on shift_logs.id). CSV "Shift ID"
    // columns may be human-readable like SHIFT-0201 from old exports — only
    // pass through when the source ID parses as a UUID, otherwise mint one.
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(shiftIdFromCSV);
    const shiftId = isUuid ? shiftIdFromCSV : crypto.randomUUID();
    const drillHoles: NonNullable<ShiftDataLike["drillHoles"]> = [];
    const consumables: NonNullable<ShiftDataLike["consumables"]> = [];

    rows.forEach((cols) => {
      if (cols[9]?.trim()) {
        const status = cols[16]?.trim();
        drillHoles.push({
          id: crypto.randomUUID(),
          holeNumber: cols[9].trim(),
          location: cols[10]?.trim() || "",
          depth: parseFloat(cols[11]) || 0,
          diameter: parseFloat(cols[12]) || 0,
          angle: parseFloat(cols[13]) || 90,
          drillingTimeSeconds: parseInt(cols[14]) || 0,
          status: status === "in-progress" || status === "abandoned" ? status : "completed",
          notes: cols[17]?.trim() || "",
          workArea: "",
          patternId: "",
        });
      }
      if (cols[18]?.trim()) {
        consumables.push({
          id: crypto.randomUUID(),
          type: cols[18].trim(),
          manufacturer: cols[19]?.trim() || "",
          name: cols[20]?.trim() || "",
          serialNumber: cols[21]?.trim() || "",
          bitDiameter: parseFloat(cols[22]) || undefined,
          faceDesign: cols[23]?.trim() || "",
          metresDrilled: parseFloat(cols[24]) || 0,
          linkedHoleIds: [],
        });
      }
    });

    const rigType = first[5]?.trim();
    logs.push({
      id: shiftId,
      shift_data: {
        shiftDetails: {
          site: "",
          date: normalizeDate(first[1] ?? ""),
          shiftType: first[2]?.trim() === "night" ? "night" : "day",
          operatorName: first[3]?.trim() || "",
          rigId: first[4]?.trim() || "",
          rigType: rigType === "D65" || rigType === "MD6250" ? rigType : "",
          startTime: first[6]?.trim() || "06:00",
          endTime: first[7]?.trim() || "18:00",
          location: first[8]?.trim() || "",
          workAreas: [],
          patternIds: [],
        },
        drillHoles,
        consumables,
        delays: [],
        notes: first[25]?.trim() || "",
        createdAt: first[26]?.trim() || now,
      },
    });
  });

  return { logs, errors };
}
